import { NonNullAssert } from '@angular/compiler';
import { Injectable } from '@angular/core';
import { isEmptyObject, parseEmptyArray } from '@common/helpers';
import { DATA_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { MListData } from '@common/interfaces';
import { AirportI18n } from '@common/interfaces/master/airport_i18n_all';
import { MSpecialMeal } from '@common/interfaces/master/m_special_meal';
import {
  canSelectAllMealByTraveler,
  InFlightMealRequestData,
  INITIAL_FLIGHT_MEAL_SERVICE_REQUEST_MASTER_DATA,
  MasterDataS02M017,
  PassengerApplicationStatusMap,
  SegmentMap,
  UpdateFlightMealInformation,
} from '@common/interfaces/mybooking/flight-meal.interface';
import { Meal, TravelerMealInfo } from '@common/interfaces/mybooking/order-services.interface';
import { LOAD_MASTERS } from '@common/master';
import { AswMasterService, CommonLibService, SystemDateService } from '@lib/services';
import { BehaviorSubject, take } from 'rxjs';
import { GetOrderResponseData, Traveler, Type1 } from 'src/sdk-servicing';

@Injectable({
  providedIn: 'root',
})
export class FlightMealService {
  mealInformationPre!: UpdateFlightMealInformation;

  /** Enable Application Submit */
  _isEnableApplicationSubmit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**trigger rerender */
  _reRender: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  masterData$ = new BehaviorSubject<MasterDataS02M017>(INITIAL_FLIGHT_MEAL_SERVICE_REQUEST_MASTER_DATA);

  /** Event Function */
  functionList!: { [key: string]: Function };

  /** セグメント別機内食選択 */
  flightsList: Type1[] = [];

  pnr!: GetOrderResponseData;
  flights: Type1[] = [];
  travelers: Traveler[] = [];

  /** 機内食選択 for All Flight of Traveler */
  canSelectAllMealByTravelers: canSelectAllMealByTraveler = {};

  inFlightMealRequestData$ = new BehaviorSubject<InFlightMealRequestData>({});

  // (1.3)	画面にて変更した内容を保持するための更新サービス情報について以下の内容を設定し、保持する。
  // (1.3.1) 以下通りに「更新サービス情報」を作成する。
  /** 更新サービス情報 */
  mealInformation: UpdateFlightMealInformation = {
    airBoundId: '',
    segmentMap: new Map<string, SegmentMap>(),
  };

  constructor(
    private _common: CommonLibService,
    private _aswMasterService: AswMasterService,
    private _systemDateSvc: SystemDateService
  ) {}

  /** Common No (1) 空港名称取得処理 */
  onHandleCommonNo1(airportCode: string): string {
    // 1.	空港名称に""(空欄)を設定する。
    let airportName = '';
    // 2.	以下の条件でASWDBの「空港(国際化)」テーブルから情報を取得する。
    this._aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.AIRPORT_I18N_ALL.key)
      .pipe(take(1))
      .subscribe((airportI18n: AirportI18n) => {
        // 3.	取得できた場合、空港名称に空港(国際化).空港名称を設定する。
        airportName = airportI18n?.[String(airportCode)]?.[0].airport_name ?? '';
      });

    // 4.	空港名称を返却する。
    return airportName;
  }

  /**
   * Common No (2) Web取扱不可取得処理
   * @Input SSRコード：申込済機内食情報のSSRコード
   * @Output 戻り値：Web取扱フラグ
   */
  onHandleCommonNo2(ssrCode: string): string {
    // [Q&A UIUXS2-12397]
    // 1.	Web取扱フラグに""(空欄)を設定する。
    let webHandlingFlag: string = '';
    // 2. システム日付取得(G03-519)よりsystemDateを取得する。
    const systemDate = this._systemDateSvc.getFormattedSystemDate('yyyyMMdd');
    // 3.	以下の条件でASWDBの「特別機内食」テーブルから情報を取得する。
    this._aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.M_SPECIAL_MEAL.key)
      .pipe(take(1))
      .subscribe((mSpecialMeal: Array<MSpecialMeal>) => {
        const _mSpecialMeal = parseEmptyArray(mSpecialMeal);
        const specialMealInfo: MSpecialMeal =
          _mSpecialMeal.find(
            (meal) =>
              // SSRコード=入力パラメータ.SSRコード AND
              meal.ssr_code === ssrCode &&
              // 適用開始日 ≦ systemDate AND
              Number(meal?.apply_from_date) <= Number(systemDate) &&
              // 適用終了日 ≧ systemDate
              Number(meal?.apply_to_date) >= Number(systemDate)
          ) ?? ({} as MSpecialMeal);
        // 4.	取得できた場合、Web取扱フラグに特別機内食.Web取扱フラグを設定する。
        webHandlingFlag = specialMealInfo.service_web_flag ?? '';
      });
    // 5.	Web取扱フラグを返却する。
    return webHandlingFlag;
  }

  /** Common No (3) 有料機内食名称取得処理 */
  onHandleCommonNo3(ssrCode?: string): string {
    // 1.	有料機内食名称に""(空欄)を設定する。
    let paidMealName: string = '';
    // 2.	以下の条件でASWDBの「汎用マスターデータ（リスト）」テーブルから情報を取得する。
    this._aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.LISTDATA_ALL.key)
      .pipe(take(1))
      .subscribe((data: MListData[]) => {
        const _data = parseEmptyArray(data);
        const mListInfo = _data?.find(
          (item) =>
            item?.data_code === DATA_CODE.PD_950 &&
            item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
            item?.value === ssrCode
        );

        // 3.	取得できた場合、有料機内食名称に汎用マスターデータ（リスト）.表示内容を設定する。
        paidMealName = mListInfo?.display_content ?? '';
      });

    // 4.	有料機内食名称を返却する。
    return paidMealName;
  }

  /** Common No (4) 特別機内食名称取得処理 */
  onHandleCommonNo4(ssrCode?: string): string {
    // 1.	特別機内食名称に""(空欄)を設定する。
    let specialMealName: string = '';
    // 3.	以下の条件でASWDBの「特別機内食(国際化)」テーブルから情報を取得する。
    this._aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.M_SPECIAL_MEAL.key)
      .pipe(take(1))
      .subscribe((data: Array<MSpecialMeal>) => {
        const _data = parseEmptyArray(data);
        const specialMealInfo =
          _data?.find((meal) => {
            return (
              meal?.ssr_code === ssrCode &&
              Number(meal?.apply_from_date) <= Number(this._systemDateSvc.getFormattedSystemDate('yyyyMMdd')) &&
              Number(meal?.apply_to_date) >= Number(this._systemDateSvc.getFormattedSystemDate('yyyyMMdd'))
            );
          }) ?? ({} as MSpecialMeal);

        // 4.	取得できた場合、特別機内食名称に特別機内食(国際化).特別機内食名称を設定する。
        specialMealName = specialMealInfo?.special_meal_name ?? '';
      });

    // 5.	特別機内食名称を返却する。
    return specialMealName;
  }

  /** Common No (5) 申込ボタン状態処理 */
  onHandleCommonNo5() {
    // 1.	申込済機内食情報をチェックする。
    const meal = this.pnr?.services?.meal as Meal;

    // <以下、サービシング共通store.PNR情報取得API応答.data.air.bounds.flightsの二重で繰り返し>(それぞれ要素が当該セグメント情報)
    this.flights.forEach((flight: Type1) => {
      // <以下、サービシング共通store.PNR情報取得API応答.data.travelersの要素繰り返し>(それぞれ要素が当該搭乗者情報)
      this.travelers.forEach((traveler: Traveler) => {
        // 1.1.	更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.Id>である情報をを編集情報とする。
        let mealUpdateInfo: null | PassengerApplicationStatusMap = null;
        const segmentMap = this.mealInformation.segmentMap?.get(flight?.id!);
        if (segmentMap?.passengerApplicationStatusMap.has(traveler?.id!)) {
          mealUpdateInfo = segmentMap?.passengerApplicationStatusMap.get(
            traveler?.id!
          ) as PassengerApplicationStatusMap;
        }
        // 1.2.	サービシング共通store.PNR情報取得API応答.data.services.meal.<当該セグメント情報.Id>.<当該搭乗者情報.Id>である情報を申込済機内食情報とする。
        const mealPNRInfo = meal?.[flight?.id!]?.[traveler?.id!] as TravelerMealInfo;
        // 1.3.	編集情報と申込済機内食情報が両方存在する場合、
        if (mealUpdateInfo && !isEmptyObject(mealPNRInfo)) {
          const appliedMealListInfo = parseEmptyArray(mealPNRInfo?.appliedMealList);
          // 1.3.1.	編集情報.更新区分=“request”(登録) かつ 申込済機内食情報.appliedMealListの中に、SSRコード= 編集情報.SSRコード　かつ 機内食種別= 編集情報.機内食種別が存在する場合、
          const existSSRCodeInMealApplied = appliedMealListInfo.some(
            (m) => m?.code === mealUpdateInfo?.ssrCode && m?.type === mealUpdateInfo?.mealType
          );
          if (mealUpdateInfo?.updateCategory === 'request' && existSSRCodeInMealApplied) {
            // 1.3.1.1.	最上位レベルから下位レベルまでの順次にチェックして、当該キーを基に、更新サービス情報に存在する場合、以下の通りに最下位レベルから削除する。
            if (this.mealInformation?.segmentMap?.has(flight?.id!)) {
              // 搭乗者別申込状況マップ
              if (this.mealInformation.segmentMap?.get(flight?.id!)?.passengerApplicationStatusMap.has(traveler?.id!)) {
                this.mealInformation.segmentMap?.get(flight?.id!)?.passengerApplicationStatusMap.delete(traveler?.id!);
              }

              if (this.mealInformation.segmentMap?.get(flight?.id!)?.passengerApplicationStatusMap.size === 0) {
                this.mealInformation.segmentMap.delete(flight?.id!);
              }
            }
          }
        }
      });
    });

    let mealInformationStore = this.objectWithNestedMapsToObject({ ...this.mealInformationPre });
    let mealInformationUpdate = this.objectWithNestedMapsToObject({ ...this.mealInformation });

    mealInformationStore = this.sortObjectKeys(mealInformationStore);
    mealInformationUpdate = this.sortObjectKeys(mealInformationUpdate);
    // 1.	申込ボタンの状態を以下の通りに切り替える。
    // 1.1.	更新サービス情報.セグメントマップ≠サービシング共通store.機内食情報.セグメントマップの場合、活性とする。
    if (JSON.stringify(mealInformationStore) !== JSON.stringify(mealInformationUpdate)) {
      this._isEnableApplicationSubmit$.next(true);
      return;
    }

    // 1.2.	上記以外の場合、非活性とする。
    this._isEnableApplicationSubmit$.next(false);
  }

  /**
   * Common No (7) 特別機内食のカテゴリ名称取得処理
   * @Input 特別機内食種別
   * @Output 特別機内食のカテゴリ名称
   */
  onHandleCommonNo7(category: string = '') {
    // 1.	特別機内食のカテゴリ名称に""(空欄)を設定する。
    let specialMealCategory = '';
    // 2.	以下の条件でASWDBの「汎用マスターデータ（リスト）」テーブルから情報を取得する。
    const mListInfo = this.masterData$?.value?.listData?.find(
      (item) =>
        item?.data_code === DATA_CODE.PD_007 &&
        item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
        item?.value === category
    );

    // 3.	取得できた場合、特別機内食のカテゴリ名称に汎用マスターデータ（リスト）.表示内容を設定する。
    specialMealCategory = mListInfo?.display_content ?? '';

    // 4.	特別機内食のカテゴリ名称を返却する。
    return specialMealCategory;
  }

  mapToObject(map: Map<any, any>): any {
    const obj: any = {};
    map.forEach((value, key) => {
      obj[key] = this.objectWithNestedMapsToObject(value);
    });
    return obj;
  }

  objectWithNestedMapsToObject(input: any): any {
    if (input instanceof Map) {
      return this.mapToObject(input);
    } else if (typeof input === 'object') {
      const newObj: any = {};
      for (const [key, value] of Object.entries(input)) {
        newObj[key] = this.objectWithNestedMapsToObject(value);
      }
      return newObj;
    }
    return input;
  }

  getTimeFromApplyFromDate(dateString: string): number {
    if (dateString === '') {
      return 0;
    }

    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);

    const date = new Date(year + '/' + month + '/' + day);

    return date.getTime();
  }

  sortObjectKeys(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(this.sortObjectKeys);
    }

    const sortedObj: any = {};
    const keys = Object.keys(obj).sort();

    for (const key of keys) {
      sortedObj[key] = this.sortObjectKeys(obj[key]);
    }

    return sortedObj;
  }

  deepClone(obj: any, map = new Map()) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (map.has(obj)) {
      return map.get(obj);
    }

    let clone: any;
    if (Array.isArray(obj)) {
      clone = [];
      map.set(obj, clone);
      for (let item of obj) {
        clone.push(this.deepClone(item, map));
      }
    } else if (obj instanceof Map) {
      clone = new Map();
      map.set(obj, clone);
      for (let [key, value] of obj) {
        clone.set(this.deepClone(key, map), this.deepClone(value, map));
      }
    } else if (obj instanceof Set) {
      clone = new Set();
      map.set(obj, clone);
      for (let item of obj) {
        clone.add(this.deepClone(item, map));
      }
    } else {
      clone = Object.create(Object.getPrototypeOf(obj));
      map.set(obj, clone);
      Object.keys(obj).forEach((key) => {
        clone[key] = this.deepClone(obj[key], map);
      });
    }

    return clone;
  }

  updateValueForCanSelectAllMealByTravelers(travelerId: string, flightId: string, enableSelect: boolean) {
    const currentValue = { ...this.canSelectAllMealByTravelers };

    if (!currentValue[travelerId]) {
      currentValue[travelerId] = {};
    }

    currentValue[travelerId][flightId] = enableSelect;

    this.canSelectAllMealByTravelers = currentValue;
  }

  /** 画面にて変更した内容を保持するための更新サービス情報の初期化処理 */
  clearMealInformation() {
    this.mealInformation = {
      airBoundId: '',
      segmentMap: new Map<string, SegmentMap>(),
    };
  }

  destroy(): void {}
}
